import React, { useMemo, useState } from "react";
import {
    Share2,
    Link as LinkIcon,
    Check,
    Mail,
} from "lucide-react";
import { toast } from "sonner";
import { track } from "@/lib/tracker";

/**
 * ShareBar
 * Botones de compartir minimalistas, alineados con la estética brutal del sitio.
 * Soporta Web Share API nativo en mobile + fallbacks por red social en desktop.
 *
 * Props:
 *  - url: URL completa a compartir (default: window.location.href)
 *  - title: título corto
 *  - text: copy potente que acompaña al link
 *  - hashtags: array opcional para X/Twitter
 *  - variant: "default" | "dark" | "inline"
 *  - source: etiqueta para tracking analítico (ej: "home_hero", "libro_page")
 */
export default function ShareBar({
    url,
    title = "El Principio del Mapa Inverso",
    text = "Un libro que se atreve a decir lo que casi nadie dice sobre la adicción y la sobriedad. 'El Principio del Mapa Inverso' de Nelson González.",
    hashtags = ["MapaInverso", "Sobriedad", "NiTanAdicto"],
    variant = "default",
    source = "generic",
}) {
    const shareUrl = url || (typeof window !== "undefined" ? window.location.href : "");
    const [copied, setCopied] = useState(false);

    const enc = encodeURIComponent;
    const links = useMemo(() => {
        const t = text;
        return {
            whatsapp: `https://wa.me/?text=${enc(`${t} ${shareUrl}`)}`,
            twitter: `https://twitter.com/intent/tweet?text=${enc(t)}&url=${enc(shareUrl)}&hashtags=${enc(hashtags.join(","))}`,
            facebook: `https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}`,
            telegram: `https://t.me/share/url?url=${enc(shareUrl)}&text=${enc(t)}`,
            threads: `https://www.threads.net/intent/post?text=${enc(`${t} ${shareUrl}`)}`,
            email: `mailto:?subject=${enc(title)}&body=${enc(`${t}\n\n${shareUrl}`)}`,
        };
    }, [text, shareUrl, title, hashtags]);

    const isDark = variant === "dark";
    const isInline = variant === "inline";

    function openShare(target, key) {
        track("cta_clicked", { id: `share_${key}`, source });
        if (target === "native" && typeof navigator !== "undefined" && navigator.share) {
            navigator
                .share({ title, text, url: shareUrl })
                .catch(() => {});
            return;
        }
        window.open(links[target], "_blank", "noopener,noreferrer,width=620,height=620");
    }

    async function copyLink() {
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            toast.success("Link copiado al portapapeles");
            track("cta_clicked", { id: "share_copy", source });
            setTimeout(() => setCopied(false), 2200);
        } catch {
            toast.error("No pudimos copiar el link");
        }
    }

    const buttons = [
        {
            key: "whatsapp",
            label: "WhatsApp",
            handler: () => openShare("whatsapp", "whatsapp"),
            // WhatsApp glyph (inline SVG — colors handled by currentColor in stroke)
            icon: (
                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden="true">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" />
                </svg>
            ),
        },
        {
            key: "twitter",
            label: "X",
            handler: () => openShare("twitter", "twitter"),
            icon: (
                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden="true">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
            ),
        },
        {
            key: "facebook",
            label: "Facebook",
            handler: () => openShare("facebook", "facebook"),
            icon: (
                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden="true">
                    <path d="M9.101 23.691v-7.98H6.627v-3.667h2.474v-1.58c0-4.085 1.848-5.978 5.858-5.978.401 0 .955.042 1.468.103a8.68 8.68 0 0 1 1.141.195v3.325a8.623 8.623 0 0 0-.653-.036 26.805 26.805 0 0 0-.733-.009c-.707 0-1.259.096-1.675.309a1.686 1.686 0 0 0-.679.622c-.258.42-.374.995-.374 1.752v1.297h3.919l-.386 2.103-.287 1.564h-3.246v8.245C19.396 23.238 24 18.179 24 12.044c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.628 3.874 10.35 9.101 11.647Z" />
                </svg>
            ),
        },
        {
            key: "telegram",
            label: "Telegram",
            handler: () => openShare("telegram", "telegram"),
            icon: (
                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden="true">
                    <path d="m9.417 15.181-.397 5.584c.568 0 .814-.244 1.109-.537l2.663-2.545 5.518 4.041c1.012.564 1.725.267 1.998-.931L23.93 3.821l.001-.002c.321-1.496-.541-2.081-1.527-1.714L2.43 9.927c-1.413.564-1.39 1.36-.241 1.724l5.151 1.604L19.32 5.668c.564-.374 1.077-.166.655.208z" />
                </svg>
            ),
        },
        {
            key: "threads",
            label: "Threads",
            handler: () => openShare("threads", "threads"),
            icon: (
                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden="true">
                    <path d="M17.43 11.36c-.084-.04-.169-.078-.255-.115-.149-2.752-1.652-4.328-4.176-4.344h-.034c-1.51 0-2.766.645-3.54 1.818l1.388.953c.578-.876 1.485-1.063 2.152-1.063h.024c.831.005 1.458.247 1.864.72.297.344.495.819.593 1.418-.737-.125-1.534-.164-2.385-.114-2.398.138-3.94 1.537-3.837 3.481.052.987.544 1.836 1.385 2.391.711.469 1.627.699 2.58.647 1.257-.069 2.243-.549 2.93-1.426.522-.665.851-1.527 1.001-2.612 1.014.612 1.766 1.418 2.181 2.387.706 1.647.747 4.354-1.461 6.561-1.935 1.933-4.262 2.769-6.92 2.489-4.117-.434-7.196-3.45-7.605-7.467-.295-2.895.41-5.354 2.097-7.31 1.81-2.097 4.398-3.214 7.682-3.214 3.31.022 5.83 1.04 7.491 3.024 1.41 1.685 2.166 4.025 2.194 6.967-.013.144-.025.288-.046.43z" />
                </svg>
            ),
        },
        {
            key: "email",
            label: "Email",
            handler: () => openShare("email", "email"),
            icon: <Mail className="w-4 h-4" />,
        },
        {
            key: "copy",
            label: copied ? "Copiado" : "Copiar link",
            handler: copyLink,
            icon: copied ? <Check className="w-4 h-4" /> : <LinkIcon className="w-4 h-4" />,
            highlight: copied,
        },
    ];

    const canNative =
        typeof navigator !== "undefined" && typeof navigator.share === "function";

    const wrapClass = isInline
        ? "flex flex-wrap gap-2 items-center"
        : isDark
          ? "border border-[var(--line)] bg-[#0d0d0d] p-5"
          : "border-2 border-black bg-[var(--paper-2)] p-5 text-black";

    return (
        <div
            className={wrapClass}
            data-testid={`share-bar-${source}`}
        >
            {!isInline && (
                <div className="flex items-center justify-between mb-3">
                    <p
                        className={`font-mono text-[10px] uppercase tracking-[0.3em] flex items-center gap-2 ${
                            isDark ? "text-[var(--warning)]" : "text-black"
                        }`}
                    >
                        <Share2 className="w-3 h-3" />
                        Compártelo
                    </p>
                    {canNative && (
                        <button
                            onClick={() => openShare("native", "native")}
                            className={`font-mono text-[10px] uppercase tracking-[0.25em] underline ${
                                isDark ? "text-[var(--paper-2)]" : "text-black"
                            }`}
                            data-testid={`share-native-${source}`}
                        >
                            Compartir nativo
                        </button>
                    )}
                </div>
            )}
            <div className="flex flex-wrap gap-2">
                {buttons.map((b) => (
                    <button
                        key={b.key}
                        onClick={b.handler}
                        title={b.label}
                        aria-label={`Compartir en ${b.label}`}
                        data-testid={`share-${b.key}-${source}`}
                        className={[
                            "inline-flex items-center gap-2 px-3 py-2 border font-mono text-[10px] uppercase tracking-[0.25em] transition-all duration-150",
                            isDark
                                ? "border-[var(--paper)] text-[var(--paper)] hover:bg-[var(--paper)] hover:text-black"
                                : "border-black text-black hover:bg-black hover:text-[var(--paper-2)]",
                            b.highlight ? "bg-[var(--warning)] text-black border-[var(--warning)]" : "",
                        ].join(" ")}
                    >
                        {b.icon}
                        <span className="hidden sm:inline">{b.label}</span>
                    </button>
                ))}
            </div>
        </div>
    );
}
